/**
 * BackupSection — onglet Paramètres > Sauvegardes.
 *
 * Spec 2026-07-19 : accès strictement réservé au Premier Admin. Permet de
 * configurer la fréquence auto (off/daily/weekly/monthly + heure UTC),
 * lancer un backup manuel, et lister les 5 derniers backups avec bouton
 * de téléchargement.
 */
import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { trackBackup } from "@/lib/toastBackup";
import {
  Save, Download, PlayCircle, Loader2, RefreshCw, HardDrive, Clock,
  CheckCircle2, XCircle, Cloud, Files, DollarSign, ClipboardCheck, Eye,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import B2AlertPanel from "./B2AlertPanel";

const FREQ_LABEL = {
  off: "Désactivée",
  daily: "Quotidienne",
  weekly: "Hebdomadaire",
  monthly: "Mensuelle",
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 1)} ${u[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function BackupSection() {
  const { user } = useAuth();
  // Spec iter94 — non Premier Admin : lecture seule sur toute la section.
  const canWrite = hasRight(user, "settings.storage"); // iter203 — droit de la matrice (plus de premier-admin en dur)
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [filesCount, setFilesCount] = useState(null);
  const [recordsCount, setRecordsCount] = useState(null);
  const [retention, setRetention] = useState(5);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, l, o] = await Promise.all([
        api.get("/settings/backup/config"),
        api.get("/settings/backup/list"),
        api.get("/settings/backup/b2-overview").catch(() => ({ data: null })),
      ]);
      setCfg(c.data);
      setItems(l.data.items || []);
      setFilesCount(l.data.files_count ?? null);
      setRecordsCount(l.data.records_count ?? null);
      setRetention(l.data.retention || 5);
      setOverview(o.data);
    } catch (err) {
      toast.error("Chargement impossible", { description: err.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/backup/config", {
        frequency: cfg.frequency,
        hour_utc: cfg.hour_utc,
      });
      setCfg(data);
      toast.success("Réglage enregistré", { description: `${FREQ_LABEL[cfg.frequency] || cfg.frequency}${cfg.frequency !== "off" ? `, ${String((cfg.hour_utc + 1) % 24).padStart(2, "0")}h00 Alger` : ""}` });
    } catch (err) {
      toast.error("Enregistrement impossible", { description: err.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const trigger = async () => {
    setRunning(true);
    try {
      await api.post("/settings/backup/trigger");
      trackBackup({ onDone: load, onRetry: trigger });
    } catch (err) {
      toast.error("Lancement impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setTimeout(() => setRunning(false), 6000);
    }
  };

  const download = async (backup) => {
    try {
      const { data } = await api.get(`/settings/backup/${backup.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = backup.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Téléchargement impossible", { description: err.response?.data?.detail || "" });
    }
  };

  if (loading || !cfg) {
    return (
      <Card className="p-10 bg-card border-vertec-borderDark flex items-center gap-3">
        <Loader2 size={22} className="animate-spin text-vertec-blue" />
        <div className="text-sm text-muted-foreground">Chargement des sauvegardes…</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-section">
      {!canWrite && (
        <div
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px]"
          style={{
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.25)",
            color: "#93C5FD",
          }}
          data-testid="backup-readonly-banner"
        >
          <Eye size={14} />
          <span>
            Mode <strong>lecture seule</strong> — seul le Premier Admin peut modifier la
            configuration, lancer une sauvegarde ou télécharger un backup.
          </span>
        </div>
      )}
      {/* --- Header global --- */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold mb-1">Sauvegardes complètes — Backblaze B2 <span className="text-sm text-muted-foreground font-normal">(cloud, source de vérité)</span></h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Archives ZIP de la base MongoDB complète, envoyées automatiquement chaque nuit dans le préfixe <code>_backups/</code>.
            Rotation réellement appliquée : <strong data-testid="b2-retention-rule">{retention} derniers · plafond {fmtBytes(overview?.retention_budget_bytes || 50 * 1024 ** 3)}</strong>. Le miroir NAS est configuré dans son propre onglet.
            {overview && overview.scheduler_enabled === false && <span className="block text-[11px] mt-0.5" style={{ color: "#eca04f" }} data-testid="b2-scheduler-off">Planificateur de backup désactivé sur cet environnement (BACKUP_SCHEDULER_ENABLED ≠ true) — normal hors production.</span>}
            <span className="block text-[11px] mt-0.5 opacity-80">
              Ces chiffres ne comptabilisent PAS les fichiers utilisateurs (documents, pièces jointes) — ceux-ci figurent dans <em>Stockage applicatif</em>.
            </span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="backup-refresh">
          <RefreshCw size={14} className={"mr-1.5 " + (loading ? "animate-spin" : "")} /> Actualiser
        </Button>
      </div>

      {/* --- Backblaze B2 --------------------------------------- */}
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-3">
          <div className="rounded-lg flex items-center justify-center"
               style={{ width: 32, height: 32, background: "rgba(0,120,212,0.12)", color: "#0078D4" }}>
            <Cloud size={16} />
          </div>
          <h3 className="text-lg font-semibold">Backblaze B2 <span className="text-sm text-muted-foreground font-normal">(cloud, source de vérité)</span></h3>
          {overview && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] ml-2"
                  style={{ background: "rgba(34,197,94,0.10)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.25)" }}
                  data-testid="b2-status-badge">
              <CheckCircle2 size={12} /> Opérationnel
            </span>
          )}
        </div>

        {/* KPI pills B2 */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="b2-kpi-row">
            <div className="rounded-lg p-4 flex items-center gap-3"
                 style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                 data-testid="b2-kpi-used">
              <HardDrive size={20} className="text-muted-foreground" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume réel (versions courantes)</div>
                <div className="text-xl font-semibold tabular-nums mt-0.5">{fmtBytes(overview.used_bytes)}</div>
              </div>
            </div>
            <div className="rounded-lg p-4 flex items-center gap-3"
                 style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                 data-testid="b2-kpi-count">
              <Files size={20} className="text-muted-foreground" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Nombre de backups</div>
                <div className="text-xl font-semibold tabular-nums mt-0.5">{overview.files_count}</div>
              </div>
            </div>
            <div className="rounded-lg p-4 flex items-center gap-3"
                 style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                 data-testid="b2-kpi-cost">
              <DollarSign size={20} className="text-muted-foreground" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Coût mensuel estimé</div>
                <div className="text-xl font-semibold tabular-nums mt-0.5">
                  ≈ {overview.monthly_cost_usd.toFixed(4)} $
                  <span className="text-sm text-muted-foreground font-normal ml-1">
                    · {overview.monthly_cost_eur.toFixed(4)} €
                  </span>
                </div>
                {overview.cost_breakdown && (
                  <div className="text-[10px] text-muted-foreground mt-1 tabular-nums" data-testid="b2-kpi-cost-breakdown">
                    Stockage : {overview.cost_breakdown.storage.usd.toFixed(4)} $ ·
                    Egress : {overview.cost_breakdown.egress.usd.toFixed(4)} $
                    <span className="opacity-70"> (mois en cours, jour {overview.cost_breakdown.egress.days_elapsed}/{overview.cost_breakdown.egress.days_in_month})</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* iter326 (D.2/D.3) — volume réel, versions cachées, bornes */}
        {overview?.versions && !overview.versions.error && (
          <div className="rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs"
               style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="b2-versions-row">
            <div data-testid="b2-hidden-versions" style={{ color: overview.versions.hidden_count ? "#eca04f" : "#4fc79c" }}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Versions cachées (facturées)</div>
              <div className="font-semibold tabular-nums mt-0.5">{overview.versions.hidden_count ? `${overview.versions.hidden_count} · ${fmtBytes(overview.versions.hidden_bytes)} ⚠` : "0 ✓"}{overview.versions.delete_markers ? <span className="font-normal opacity-70"> · {overview.versions.delete_markers} marqueur(s)</span> : null}</div>
            </div>
            <div data-testid="b2-oldest-backup">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Plus ancien backup</div>
              <div className="font-semibold tabular-nums mt-0.5">{overview.versions.oldest_at ? new Date(overview.versions.oldest_at).toLocaleString("fr-FR") : "—"}</div>
              <div className="text-[10px] text-muted-foreground truncate">{overview.versions.oldest_key}</div>
            </div>
            <div data-testid="b2-newest-backup">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Plus récent backup</div>
              <div className="font-semibold tabular-nums mt-0.5">{overview.versions.newest_at ? new Date(overview.versions.newest_at).toLocaleString("fr-FR") : "—"}</div>
              <div className="text-[10px] text-muted-foreground truncate">{overview.versions.newest_key}</div>
            </div>
          </div>
        )}

        {/* Dernier backup */}
        {overview?.latest_backup && (
          <div className="rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap"
               style={{ background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.20)" }}
               data-testid="b2-latest-backup">
            <ClipboardCheck size={16} className="text-status-success shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dernier backup</div>
              <div className="text-sm mt-0.5">
                <code className="font-mono text-[12px]">{overview.latest_backup.filename}</code>
                <span className="text-muted-foreground"> · {fmtDate(overview.latest_backup.created_at)} · {fmtBytes(overview.latest_backup.size)}</span>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-status-success text-xs">
              <CheckCircle2 size={12} /> Réussi
            </span>
          </div>
        )}

        {/* Seuil d'alerte de coût mensuel B2 */}
        <B2AlertPanel />
      </div>

      {/* --- Config card --- */}
      <Card className="p-6 bg-card border-vertec-borderDark" data-testid="backup-config-card">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-vertec-blue" />
          <h3 className="text-base font-semibold">Fréquence automatique</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-xs text-muted-foreground">Fréquence</Label>
            <Select
              value={cfg.frequency}
              onValueChange={(v) => setCfg({ ...cfg, frequency: v })}
              disabled={!canWrite}
            >
              <SelectTrigger data-testid="backup-freq-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Désactivée</SelectItem>
                <SelectItem value="daily">Quotidienne</SelectItem>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="monthly">Mensuelle</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            {/* iter410 L1-5 — affichage en heure d'Alger (UTC+1), stockage UTC inchangé (hour_utc) */}
            <Label className="text-xs text-muted-foreground">Heure d&apos;exécution (Alger)</Label>
            <Select
              value={String(cfg.hour_utc)}
              onValueChange={(v) => setCfg({ ...cfg, hour_utc: Number(v) })}
              disabled={!canWrite || cfg.frequency === "off"}
            >
              <SelectTrigger data-testid="backup-hour-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }).map((_, h) => (
                  <SelectItem key={h} value={String(h)} data-testid={`backup-hour-${h}`}>{String((h + 1) % 24).padStart(2, "0")}h00</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="backup-hour-utc-hint">
              UTC {String(cfg.hour_utc).padStart(2, "0")}h00
            </p>
          </div>

          <div className="flex items-end">
            <Button
              onClick={saveCfg}
              disabled={!canWrite || saving}
              className="w-full gap-1.5"
              data-testid="backup-save-cfg"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer
            </Button>
          </div>
        </div>

        <div className="text-xs text-muted-foreground grid grid-cols-1 md:grid-cols-2 gap-2 pt-3 border-t border-vertec-borderDark">
          <div data-testid="backup-last-attempt">
            <span className="text-[11px] uppercase tracking-wider">Dernière tentative :</span>{" "}
            <strong className="text-foreground">{fmtDate(cfg.last_run_at)}</strong>
            {cfg.last_status && (
              <span
                className={`ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  cfg.last_status === "ok" ? "bg-status-success/15 text-status-success"
                  : cfg.last_status === "running" ? "bg-vertec-blue/15 text-vertec-blue"
                  : "bg-status-danger/15 text-status-danger"
                }`}
                title={cfg.last_error || ""}
                data-testid="backup-last-status"
              >
                {cfg.last_status === "ok" ? "Réussi" : cfg.last_status === "running" ? "En cours…" : "Échec"}
              </span>
            )}
            {cfg.last_status === "running" && canWrite && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/settings/backup/reset-lock");
                    toast.success(data.cleared ? "Verrou libéré — vous pouvez relancer un backup." : "Aucun verrou résiduel.");
                    load();
                  } catch (err) { toast.error("Déblocage impossible", { description: err.response?.data?.detail || "" }); }
                }}
                className="ml-2 text-[10px] underline text-status-warning hover:text-foreground"
                title="Marquer le run interrompu comme échoué et libérer le verrou"
                data-testid="backup-reset-lock-btn"
              >
                Débloquer
              </button>
            )}
            {cfg.last_status === "failed" && cfg.last_error && (
              <div className="text-[11px] text-status-danger mt-0.5 truncate" title={cfg.last_error}>{cfg.last_error}</div>
            )}
          </div>
          <div data-testid="backup-last-success">
            <span className="text-[11px] uppercase tracking-wider">Dernier backup réussi :</span>{" "}
            {/* iter376 pt 2a — même heure que la dernière tentative réussie (heure de création du backup = nom du fichier) */}
            <strong className="text-foreground">{fmtDate(cfg.last_status === "ok" && cfg.last_run_at ? cfg.last_run_at : cfg.last_success_at)}</strong>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wider">Actuellement :</span>{" "}
            <strong className={cfg.frequency === "off" ? "text-status-danger" : "text-status-success"}>
              {FREQ_LABEL[cfg.frequency]}
              {cfg.frequency !== "off" && ` — ${String((cfg.hour_utc + 1) % 24).padStart(2, "0")}h00 Alger (UTC ${String(cfg.hour_utc).padStart(2, "0")}h00)`}
            </strong>
          </div>
        </div>
      </Card>

      {/* --- Générer maintenant --- */}
      <Card className="p-6 bg-card border-vertec-borderDark" data-testid="backup-manual-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HardDrive size={16} className="text-vertec-blue" />
              <h3 className="text-base font-semibold">Générer un backup maintenant</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Utile avant une opération sensible ou pour disposer d&apos;une copie fraîche à télécharger sur votre NAS.
            </p>
          </div>
          <Button
            onClick={trigger}
            disabled={!canWrite || running}
            className="gap-1.5 bg-status-success hover:bg-status-success/90"
            data-testid="backup-trigger-btn"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            {running ? "En cours…" : "Générer maintenant"}
          </Button>
        </div>
      </Card>

      {/* --- Liste des backups --- */}
      <Card className="p-0 bg-card border-vertec-borderDark overflow-hidden" data-testid="backup-list-card">
        <div className="px-6 pt-5 pb-3 border-b border-vertec-borderDark flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">Backups disponibles</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {filesCount ?? items.length}/{retention} conservés — les plus anciens sont purgés au prochain backup.{filesCount != null && recordsCount != null && filesCount !== recordsCount && <span title="Fichiers présents dans B2 sans fiche en base (ou l'inverse)." data-testid="backup-count-mismatch"> · {recordsCount} fiche(s) en base</span>}
            </p>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Aucun backup pour l&apos;instant. Cliquez sur « Générer maintenant » pour en créer un.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-vertec-borderDark bg-vertec-borderDark/20">
                <th className="text-left px-6 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Nom</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Créé le</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Type</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Contenu</th>
                <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">Taille</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, idx) => (
                <tr key={b.id} className="border-b border-vertec-borderDark/50 hover:bg-vertec-borderDark/10"
                  data-testid={`backup-row-${idx}`}>
                  <td className="px-6 py-3 font-mono text-[12px] text-foreground">{b.filename}
                    {b.orphan && <span className="ml-2 inline-flex items-center align-middle font-sans" title="Backup présent dans B2, non enregistré en base (panne du 23/10)" style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, border: "0.5px solid #4a4a52", color: "#9a9aa3", background: "#1e1e24" }} data-testid={`backup-orphan-${idx}`}>Sans fiche</span>}</td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-[12px]">
                    {b.orphan ? <span className="text-muted-foreground">—</span> : b.is_manual
                      ? <span className="inline-flex items-center gap-1 text-vertec-blue"><PlayCircle size={11}/> Manuel</span>
                      : <span className="inline-flex items-center gap-1 text-status-success"><CheckCircle2 size={11}/> Auto</span>}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-muted-foreground">
                    {b.orphan ? <span title="Backup présent dans B2, non enregistré en base (panne du 23/10)">contenu inconnu (sans fiche)</span> : <>{b.counts?.documents ?? 0} docs · {b.counts?.tasks ?? 0} tâches · {b.counts?.attachments ?? 0} fichiers</>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px]">{fmtBytes(b.size)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 border-vertec-blue/40 text-vertec-blue hover:bg-vertec-blue/10"
                      onClick={() => download(b)}
                      disabled={!canWrite}
                      title={canWrite ? "" : "Réservé au Premier Admin"}
                      data-testid={`backup-download-${idx}`}
                    >
                      <Download size={13} /> Télécharger
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
